import { useEffect, useMemo, useState } from "react";

export type TokenSiteLocale = "zh-TW" | "en" | "ja" | "ko" | "hi";

const STORAGE_KEY = "knab.tokenSite.locale";
const EVENT_NAME = "knab-token-site-locale";

const OPTIONS: { value: TokenSiteLocale; label: string }[] = [
  { value: "zh-TW", label: "繁體中文" },
  { value: "en", label: "English" },
  { value: "ja", label: "日本語" },
  { value: "ko", label: "한국어" },
  { value: "hi", label: "हिन्दी" },
];

const HTML_LANG: Record<TokenSiteLocale, string> = {
  "zh-TW": "zh-Hant",
  en: "en",
  ja: "ja",
  ko: "ko",
  hi: "hi",
};

const NAV_TEXT: Record<TokenSiteLocale, {
  home: string;
  dashboard: string;
  reward: string;
  language: string;
  useBase: string;
  pricePerToken: string;
  myToken: string;
  estimatedValue: string;
  wallet: string;
  spend: string;
  sell: string;
  estimatedBuy: string;
  estimatedReceive: string;
  quoteUnavailable: string;
}> = {
  "zh-TW": {
    home: "首頁",
    dashboard: "儀表板",
    reward: "獎勵",
    language: "語言",
    useBase: "在 BASE 使用 USDC",
    pricePerToken: "價格 / 1",
    myToken: "我的",
    estimatedValue: "估算價值",
    wallet: "錢包",
    spend: "花費",
    sell: "賣出",
    estimatedBuy: "預計買入",
    estimatedReceive: "預計得到",
    quoteUnavailable: "等待價格",
  },
  en: {
    home: "Home",
    dashboard: "Dashboard",
    reward: "Reward",
    language: "Language",
    useBase: "Use USDC on BASE",
    pricePerToken: "PRICE / 1",
    myToken: "MY",
    estimatedValue: "EST. VALUE",
    wallet: "WALLET",
    spend: "SPEND",
    sell: "SELL",
    estimatedBuy: "EST. BUY",
    estimatedReceive: "EST. RECEIVE",
    quoteUnavailable: "WAITING FOR PRICE",
  },
  ja: {
    home: "ホーム",
    dashboard: "ダッシュボード",
    reward: "報酬",
    language: "言語",
    useBase: "BASE で USDC を使う",
    pricePerToken: "価格 / 1",
    myToken: "保有",
    estimatedValue: "概算価値",
    wallet: "ウォレット",
    spend: "支払う",
    sell: "売る",
    estimatedBuy: "購入予定",
    estimatedReceive: "受取予定",
    quoteUnavailable: "価格待ち",
  },
  ko: {
    home: "홈",
    dashboard: "대시보드",
    reward: "리워드",
    language: "언어",
    useBase: "BASE에서 USDC 사용",
    pricePerToken: "가격 / 1",
    myToken: "내",
    estimatedValue: "예상 가치",
    wallet: "지갑",
    spend: "지불",
    sell: "판매",
    estimatedBuy: "예상 구매",
    estimatedReceive: "예상 수령",
    quoteUnavailable: "가격 대기",
  },
  hi: {
    home: "होम",
    dashboard: "डैशबोर्ड",
    reward: "रिवॉर्ड",
    language: "भाषा",
    useBase: "BASE पर USDC इस्तेमाल करें",
    pricePerToken: "कीमत / 1",
    myToken: "मेरे",
    estimatedValue: "अनुमानित मूल्य",
    wallet: "वॉलेट",
    spend: "खर्च",
    sell: "बेचें",
    estimatedBuy: "अनुमानित खरीद",
    estimatedReceive: "अनुमानित प्राप्ति",
    quoteUnavailable: "कीमत की प्रतीक्षा",
  },
};

function isLocale(value: string | null): value is TokenSiteLocale {
  return !!value && OPTIONS.some((option) => option.value === value);
}

function getInitialLocale(): TokenSiteLocale {
  if (typeof window === "undefined") return "en";

  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (isLocale(saved)) return saved;

  const browser = window.navigator.language.toLowerCase();
  if (browser.startsWith("zh")) return "zh-TW";
  if (browser.startsWith("ja")) return "ja";
  if (browser.startsWith("ko")) return "ko";
  if (browser.startsWith("hi")) return "hi";
  return "en";
}

export function useTokenSiteText() {
  const [locale, setLocaleState] = useState<TokenSiteLocale>(getInitialLocale);

  function setLocale(nextLocale: TokenSiteLocale) {
    setLocaleState(nextLocale);
    window.localStorage.setItem(STORAGE_KEY, nextLocale);
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: nextLocale }));
  }

  useEffect(() => {
    document.documentElement.lang = HTML_LANG[locale];
  }, [locale]);

  useEffect(() => {
    function onLocaleChange(event: Event) {
      const detail = (event as CustomEvent).detail;
      if (isLocale(detail)) setLocaleState(detail);
    }

    window.addEventListener(EVENT_NAME, onLocaleChange);
    return () => window.removeEventListener(EVENT_NAME, onLocaleChange);
  }, []);

  return useMemo(
    () => ({
      locale,
      setLocale,
      text: NAV_TEXT[locale],
    }),
    [locale]
  );
}

export function LanguageSwitcher() {
  const { locale, setLocale, text } = useTokenSiteText();

  return (
    <label className="flex items-center gap-1 text-xs font-semibold text-white">
      <span className="hidden md:inline">{text.language}</span>
      <select
        value={locale}
        aria-label={text.language}
        onChange={(event) => setLocale(event.target.value as TokenSiteLocale)}
        className="max-w-[8rem] rounded-md border border-white/20 bg-black/55 px-2 py-1 text-xs text-white shadow-sm backdrop-blur outline-none hover:border-white/45"
      >
        {OPTIONS.map((option) => (
          <option key={option.value} value={option.value} className="bg-black text-white">
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
